import {groupByUniqueKey, objectFromEntries} from '@benchling/js-utils';

import {AssemblySelector} from 'molbio/bulk-assembly/AssemblySelector';
import {
  BulkAssemblyBin,
  BulkAssemblyFragmentBin,
  BulkAssemblyProcedure,
  ConstructFragment,
  FragmentProductionMethod,
  isBulkAssemblyFragmentBin,
  NormalizedConstruct,
  NormalizedFragment,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import BulkAssemblyPolymerData from 'molbio/bulk-assembly/BulkAssemblyPolymerData';
import {BA_EMPTY_VALUE, baRawText, isBAId, isEmptyBAValue} from 'molbio/bulk-assembly/BulkAssemblyValues';

export enum BulkAssemblyDestructiveAction {
  DELETE_PREFERRED_PRIMERS = 'DELETE_PREFERRED_PRIMERS',
}

export type DestructiveActionsForApproval = {
  [destructiveAction in BulkAssemblyDestructiveAction]?: Array<string>;
};

interface ResolveBinsResult {
  newBinCidOrdering: Array<string>;
  newBinsByCid: {[cid: string]: BulkAssemblyBin};
  fragmentsUpdatedByBin: Array<NormalizedFragment>;
  destructiveActionsForApproval: DestructiveActionsForApproval;
}

/**
 * Class responsible for handling updates to bulk assembly state.
 */
export default class BulkAssemblyStateManager {
  existingFragmentsByCid: {[id: string]: NormalizedFragment};
  existingStateSelector: AssemblySelector;
  constructor(
    readonly existingState: BulkAssemblyProcedure,
    readonly polymerData: BulkAssemblyPolymerData
  ) {
    this.existingFragmentsByCid = groupByUniqueKey(this.existingState.fragments, (fragment) => fragment.cid);
    this.existingStateSelector = new AssemblySelector(existingState);
  }

  /**
   * Given a tentative bulk assembly procedure, clean it up before storing in Redux.
   *
   * Currently, this function detects and handles:
   * - any now-invalid ID references, similar to cascade-delete in a database, but instead downgrading to raw
   *   text of the last known name
   * - For some destructive actions, like deleting preferred primers as a result of changing
   *   fragment production method, return warning(s) to be shown to the user to confirm. (No destructive side
   *   effects will be performed unless explicitly approved by the user.)
   *
   * TODO:
   * - Resolve all string references to ID if possible. For example, renaming a bin should resolve
   *   all existing raw text bin references with that name.
   */
  resolveState(proposedState: BulkAssemblyProcedure): {
    resolvedState: BulkAssemblyProcedure;
    destructiveActions: DestructiveActionsForApproval;
  } {
    const proposedStateSelector = new AssemblySelector(proposedState);
    const {newBinCidOrdering, newBinsByCid, fragmentsUpdatedByBin, destructiveActionsForApproval} =
      this.resolveBins(
        proposedState.binCidOrdering,
        proposedState.binsByCid,
        proposedState.fragments,
        proposedStateSelector
      );
    const newBinCids = new Set(newBinCidOrdering);
    const newFragments = fragmentsUpdatedByBin.map((fragment) =>
      this.resolveFragment(fragment, newBinCids, proposedStateSelector)
    );
    const binFragmentKeys = new Set(
      newFragments.flatMap((fragment) =>
        isBAId(fragment.bin) ? [this.binFragmentKey(fragment.bin.id, fragment.cid)] : []
      )
    );

    const newConstructs = proposedState.constructs.map((construct) =>
      this.resolveConstruct(construct, newBinCids, binFragmentKeys)
    );

    return {
      resolvedState: {
        ...proposedState,
        binCidOrdering: newBinCidOrdering,
        binsByCid: newBinsByCid,
        fragments: newFragments,
        constructs: newConstructs,
      },
      destructiveActions: destructiveActionsForApproval,
    };
  }

  /**
   * String key for a bin/fragment ID pair.
   */
  private binFragmentKey(binId: string, fragmentId: string): string {
    return `${binId}:${fragmentId}`;
  }

  /**
   * Called when making a change to a fragment bin to verify if the bin changes have any side effects
   * on the assembly's fragments and what changes should be made.
   *
   * @param updatedBin Bin with the intended updated data and the same CID as the bin being changed
   * @returns The full list of fragments with side effects applied and the applicable warnings,
   *          or null if no fragment changes need to be made.
   */
  private getUpdatedFragmentsOnBinChange(
    bin: BulkAssemblyFragmentBin,
    fragments: Array<NormalizedFragment>,
    existingAssemblySelector: AssemblySelector
  ): {
    updatedFragments: Array<NormalizedFragment>;
    warnings: Array<string>;
  } | null {
    // If the updated bin has a fragment production method of "use primer pair", no action is needed
    if (bin.fragmentProductionMethod === FragmentProductionMethod.PRIMER_PAIR) {
      return null;
    }
    let hasFragmentsToUpdate = false;
    const updatedFragments: Array<NormalizedFragment> = [];
    for (const fragment of fragments) {
      if (
        isBAId(fragment.bin) &&
        fragment.bin.id === bin.cid &&
        (!isEmptyBAValue(fragment.preferredPrimer5) || !isEmptyBAValue(fragment.preferredPrimer3))
      ) {
        hasFragmentsToUpdate = true;
        updatedFragments.push({
          ...fragment,
          preferredPrimer5: BA_EMPTY_VALUE,
          preferredPrimer3: BA_EMPTY_VALUE,
        });
      } else {
        updatedFragments.push({...fragment});
      }
    }
    if (!hasFragmentsToUpdate) {
      return null;
    }
    return {
      updatedFragments,
      warnings: [`This change will remove the preferred primers you've specified for bin ${bin.name}.`],
    };
  }

  private resolveBins(
    binCidOrdering: Array<string>,
    binsByCid: {[cid: string]: BulkAssemblyBin},
    fragments: Array<NormalizedFragment>,
    proposedStateSelector: AssemblySelector
  ): ResolveBinsResult {
    let newFragments = [...fragments];
    const warnings: Array<string> = [];
    for (const currentBin of Object.values(binsByCid)) {
      if (!isBulkAssemblyFragmentBin(currentBin)) {
        continue;
      }
      const updatedFragmentsResult = this.getUpdatedFragmentsOnBinChange(
        currentBin,
        newFragments,
        proposedStateSelector
      );
      if (updatedFragmentsResult == null) {
        continue;
      }
      newFragments = updatedFragmentsResult.updatedFragments;
      warnings.push(...updatedFragmentsResult.warnings);
    }
    const result: ResolveBinsResult = {
      newBinCidOrdering: binCidOrdering,
      newBinsByCid: binsByCid,
      fragmentsUpdatedByBin: newFragments,
      destructiveActionsForApproval: {},
    };
    if (warnings.length > 0) {
      result.destructiveActionsForApproval = {
        [BulkAssemblyDestructiveAction.DELETE_PREFERRED_PRIMERS]: warnings,
      };
    }
    return result;
  }

  private updateFragmentForRestrictionEnzymes(
    draftFragment: NormalizedFragment,
    proposedStateSelector: AssemblySelector
  ): NormalizedFragment {
    if (!this.existingFragmentsByCid.hasOwnProperty(draftFragment.cid)) {
      return draftFragment;
    }
    const existingFragmentProductionMethod = this.existingStateSelector.getFragmentProductionMethodForBin(
      this.existingFragmentsByCid[draftFragment.cid].bin
    );
    const fragmentProductionMethod = proposedStateSelector.getFragmentProductionMethodForBin(
      draftFragment.bin
    );
    if (existingFragmentProductionMethod !== fragmentProductionMethod) {
      return {
        ...draftFragment,
        restrictionEnzyme5: BA_EMPTY_VALUE,
        restrictionEnzyme3: BA_EMPTY_VALUE,
      };
    }
    return draftFragment;
  }

  private resolveFragment(
    fragment: NormalizedFragment,
    newBinCids: Set<string>,
    proposedStateSelector: AssemblySelector
  ): NormalizedFragment {
    let draftFragment: NormalizedFragment = {...fragment};
    // First downgrade any bin references to raw text, if the bin has since been deleted.
    if (isBAId(draftFragment.bin) && !newBinCids.has(draftFragment.bin.id)) {
      draftFragment = {
        ...draftFragment,
        bin: baRawText(this.lastKnownBinName(draftFragment.bin.id)),
      };
    }
    draftFragment = this.updateFragmentForRestrictionEnzymes(draftFragment, proposedStateSelector);
    return draftFragment;
  }

  private resolveConstruct(
    construct: NormalizedConstruct,
    newBinCids: Set<string>,
    binFragmentKeys: Set<string>
  ): NormalizedConstruct {
    return {
      ...construct,
      constructFragmentsByBinId: objectFromEntries(
        Object.entries(construct.constructFragmentsByBinId).flatMap(([binCid, constructFragment]) => {
          if (!newBinCids.has(binCid)) {
            // Bin must have been deleted, so remove the corresponding info from this construct.
            return [];
          }
          return [[binCid, this.resolveConstructFragment(binCid, constructFragment, binFragmentKeys)]];
        })
      ),
    };
  }

  private resolveConstructFragment(
    binCid: string,
    constructFragment: ConstructFragment,
    binFragmentKeys: Set<string>
  ): ConstructFragment {
    if (
      isBAId(constructFragment.fragment) &&
      !binFragmentKeys.has(this.binFragmentKey(binCid, constructFragment.fragment.id))
    ) {
      return {fragment: baRawText(this.lastKnownFragmentName(constructFragment.fragment.id))};
    } else {
      return constructFragment;
    }
  }

  /**
   * Determine a best-effort string substitute for a bin ID that no longer exists.
   */
  private lastKnownBinName(binCid: string): string {
    if (this.existingState.binsByCid.hasOwnProperty(binCid)) {
      return this.existingState.binsByCid[binCid].name;
    } else {
      return '';
    }
  }

  /**
   * Determine a best-effort string substitute for a fragment ID that no longer exists or is no
   * longer in the right bin.
   */
  private lastKnownFragmentName(fragmentCid: string): string {
    if (this.existingFragmentsByCid.hasOwnProperty(fragmentCid)) {
      const existingFragment = this.existingFragmentsByCid[fragmentCid];
      if (isBAId(existingFragment.sequence)) {
        return this.polymerData.getName(existingFragment.sequence.id) ?? '';
      } else if (isBAId(existingFragment.protein)) {
        return this.polymerData.getName(existingFragment.protein.id) ?? '';
      }
    }
    return '';
  }
}
